/**
 * Crypto utilities for secure token storage and encryption
 * Uses Web Crypto API for browser-native encryption
 */

class CryptoUtils {
  constructor() {
    this.algorithm = 'AES-GCM';
    this.keyLength = 256;
    this.ivLength = 12; // 96 bits for GCM
    
    // Check if Web Crypto API is available
    this.isWebCryptoAvailable = typeof crypto !== 'undefined' && crypto.subtle;
    
    if (!this.isWebCryptoAvailable) {
      console.warn('Web Crypto API not available. Using fallback encryption methods.');
    }
  }

  /**
   * Generate a cryptographic key for encryption
   * @returns {Promise<CryptoKey|Object>}
   */
  async generateKey() {
    if (!this.isWebCryptoAvailable) {
      // Fallback: return a simple object that mimics the crypto key interface
      return {
        type: 'fallback',
        algorithm: this.algorithm,
        extractable: false,
        usages: ['encrypt', 'decrypt']
      };
    }
    
    return await crypto.subtle.generateKey(
      {
        name: this.algorithm,
        length: this.keyLength,
      },
      false, // Not extractable for security
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Derive a key from a password using PBKDF2
   * @param {string} password 
   * @param {Uint8Array} salt 
   * @returns {Promise<CryptoKey>}
   */
  async deriveKey(password, salt) {
    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(password);

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000, // High iteration count for security
        hash: 'SHA-256',
      },
      keyMaterial,
      {
        name: this.algorithm,
        length: this.keyLength,
      },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Generate a random salt
   * @returns {Uint8Array}
   */
  generateSalt() {
    if (!this.isWebCryptoAvailable) {
      // Fallback: use Math.random (less secure but functional)
      const salt = new Uint8Array(16);
      for (let i = 0; i < 16; i++) {
        salt[i] = Math.floor(Math.random() * 256);
      }
      return salt;
    }
    return crypto.getRandomValues(new Uint8Array(16));
  }

  /**
   * Generate a random IV for encryption
   * @returns {Uint8Array}
   */
  generateIV() {
    if (!this.isWebCryptoAvailable) {
      // Fallback: use Math.random (less secure but functional)
      const iv = new Uint8Array(this.ivLength);
      for (let i = 0; i < this.ivLength; i++) {
        iv[i] = Math.floor(Math.random() * 256);
      }
      return iv;
    }
    return crypto.getRandomValues(new Uint8Array(this.ivLength));
  }

  /**
   * Encrypt data using AES-GCM
   * @param {string} data 
   * @param {CryptoKey|Object} key 
   * @returns {Promise<{encrypted: ArrayBuffer, iv: Uint8Array}>}
   */
  async encrypt(data, key) {
    if (!this.isWebCryptoAvailable || key.type === 'fallback') {
      // Fallback: simple base64 encoding (not secure, but prevents errors)
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);
      const iv = this.generateIV();
      
      return {
        encrypted: dataBuffer,
        iv: iv
      };
    }
    
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const iv = this.generateIV();

    const encrypted = await crypto.subtle.encrypt(
      {
        name: this.algorithm,
        iv: iv,
      },
      key,
      dataBuffer
    );

    return {
      encrypted,
      iv
    };
  }

  /**
   * Decrypt data using AES-GCM
   * @param {ArrayBuffer} encryptedData 
   * @param {Uint8Array} iv 
   * @param {CryptoKey|Object} key 
   * @returns {Promise<string>}
   */
  async decrypt(encryptedData, iv, key) {
    if (!this.isWebCryptoAvailable || key.type === 'fallback') {
      // Fallback: simple decoding (not secure, but prevents errors)
      const decoder = new TextDecoder();
      return decoder.decode(encryptedData);
    }
    
    const decrypted = await crypto.subtle.decrypt(
      {
        name: this.algorithm,
        iv: iv,
      },
      key,
      encryptedData
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  }

  /**
   * Convert ArrayBuffer to base64 string for storage
   * @param {ArrayBuffer} buffer 
   * @returns {string}
   */
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Convert base64 string to ArrayBuffer
   * @param {string} base64 
   * @returns {ArrayBuffer}
   */
  base64ToArrayBuffer(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Hash data using SHA-256
   * @param {string} data 
   * @returns {Promise<string>}
   */
  async hash(data) {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    return this.arrayBufferToBase64(hashBuffer);
  }

  /**
   * Generate a device fingerprint for additional security
   * @returns {Promise<string>}
   */
  async generateDeviceFingerprint() {
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      window.screen.width + 'x' + window.screen.height,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency || 'unknown',
      navigator.platform
    ].join('|');

    return await this.hash(fingerprint);
  }

  /**
   * Validate data integrity using HMAC
   * @param {string} data 
   * @param {string} signature 
   * @param {CryptoKey} key 
   * @returns {Promise<boolean>}
   */
  async verifyIntegrity(data, signature, key) {
    try {
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);
      
      const computedSignature = await crypto.subtle.sign(
        'HMAC',
        key,
        dataBuffer
      );

      const providedSignature = this.base64ToArrayBuffer(signature);
      
      // Compare the computed signature with the provided signature
      const computedSignatureArray = new Uint8Array(computedSignature);
      const providedSignatureArray = new Uint8Array(providedSignature);
      
      if (computedSignatureArray.length !== providedSignatureArray.length) {
        return false;
      }
      
      // Constant-time comparison to prevent timing attacks
      let result = 0;
      for (let i = 0; i < computedSignatureArray.length; i++) {
        result |= computedSignatureArray[i] ^ providedSignatureArray[i];
      }
      
      return result === 0;
    } catch (error) {
      console.error('Integrity verification failed:', error);
      return false;
    }
  }

  /**
   * Secure random string generation
   * @param {number} length 
   * @returns {string}
   */
  generateSecureRandomString(length = 32) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    
    if (!this.isWebCryptoAvailable) {
      // Fallback: use Math.random (less secure but functional)
      let result = '';
      for (let i = 0; i < length; i++) {
        result += chars[Math.floor(Math.random() * chars.length)];
      }
      return result;
    }
    
    const randomBytes = crypto.getRandomValues(new Uint8Array(length));
    let result = '';
    
    for (let i = 0; i < length; i++) {
      result += chars[randomBytes[i] % chars.length];
    }
    
    return result;
  }

  /**
   * Generate an encryption key for token storage
   * @returns {Promise<CryptoKey|Object>}
   */
  async generateEncryptionKey() {
    if (!this.isWebCryptoAvailable) {
      // Fallback: return a simple object that mimics the crypto key interface
      return {
        type: 'fallback',
        algorithm: this.algorithm,
        extractable: true,
        usages: ['encrypt', 'decrypt']
      };
    }
    
    return await crypto.subtle.generateKey(
      {
        name: this.algorithm,
        length: this.keyLength,
      },
      true, // Extractable so we can store it
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Export a crypto key to JSON format for storage
   * @param {CryptoKey|Object} key 
   * @returns {Promise<Object>}
   */
  async exportKey(key) {
    if (!this.isWebCryptoAvailable || key.type === 'fallback') {
      // Fallback: return the key object as-is
      return key;
    }
    
    const exported = await crypto.subtle.exportKey('jwk', key);
    return exported;
  }

  /**
   * Import a crypto key from JSON format
   * @param {Object} keyData 
   * @returns {Promise<CryptoKey|Object>}
   */
  async importKey(keyData) {
    if (!this.isWebCryptoAvailable || keyData.type === 'fallback') {
      // Fallback: return the key data as-is
      return keyData;
    }
    
    return await crypto.subtle.importKey(
      'jwk',
      keyData,
      {
        name: this.algorithm,
        length: this.keyLength,
      },
      true,
      ['encrypt', 'decrypt']
    );
  }
}

// Export singleton instance
export const cryptoUtils = new CryptoUtils();
export default cryptoUtils;